import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import api from "./apiClient";

import cookiesHelper from "../helpers/cookies";

import { HMICreateAssessment, HMIDeleteJobDescription, HMIDeleteResume, HMIIInviteFromAssessment, HMIUpdateAssessment, HMIUploadJobDescription, HMIUploadResume, IAssessmentDetails, IAssessmentListItem, ISortQuery } from "../interfaces/services";
import { EtyAssessment } from "../interfaces/entities";

import useToast from "./useToast";
import { useCalculateAssessmentPrograms } from "./assessment_programs";

export const useGetAssessments = (sortQuery: ISortQuery) => {
    const { sort_key, sort_order } = sortQuery;

    const queryFn = async (): Promise<IAssessmentListItem[]> => {
        const token = cookiesHelper.getCookie(`${process.env.REACT_APP_AUTH_TOKEN_NAME}`);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const response = await api.get(`assessments`, { params: { ...sortQuery } });
        return response.data;
    };

    return useQuery({
        queryKey: ['assessments_list', sort_key, sort_order],
        queryFn,
        retry: 0,
        enabled: !!sort_key && !!sort_order
    });
};

export const useCreateAssessment = () => {
    const { addToast } = useToast();
    const navigate = useNavigate();

    const { mutate: calculateAssessmentPrograms } = useCalculateAssessmentPrograms();

    const mutationFn = async (data: HMICreateAssessment): Promise<EtyAssessment> => {
        const token = cookiesHelper.getCookie(`${process.env.REACT_APP_AUTH_TOKEN_NAME}`);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const response = await api.post(
            '/assessments',
            data.payload,
            { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        return response.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsProcessing(true);
        },
        onSuccess: (data, variables) => {
            addToast({
                type: 'success',
                title: 'Assessment created successfully.',
            });

            variables.setAssessmentId(data.id);

            if (!data.foreign_worker_id) {
                variables.setMainView('invite');

                calculateAssessmentPrograms({
                    assessmentId: data.id,
                    setIsProcessing: variables.setIsProcessing,
                });
            } else {
                calculateAssessmentPrograms({
                    assessmentId: data.id,
                    setIsProcessing: variables.setIsProcessing,
                    successCallback: () => {
                        navigate(`/assessments/${data.id}/immigration-recommendations`);
                    },
                });
            };
        },
        onError: (error: any) => {
            addToast({
                type: 'error',
                title: error.response.data.message,
            });
        },
        onSettled: (_, __, variables) => {
            variables.setIsProcessing(false);
        },
    });
};

export const useUpdateAssessment = () => {
    const { addToast } = useToast();
    const navigate = useNavigate();

    const queryClient = useQueryClient();

    const { mutate: calculateAssessmentPrograms } = useCalculateAssessmentPrograms();

    const mutationFn = async (data: HMIUpdateAssessment): Promise<EtyAssessment> => {
        const token = cookiesHelper.getCookie(`${process.env.REACT_APP_AUTH_TOKEN_NAME}`);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const response = await api.patch(`assessments/update/${data.payload.id}`, data.payload);
        return response.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsProcessing(true);
        },
        onSuccess: (data, variables) => {
            addToast({
                type: 'success',
                title: 'Assessment updated successfully.',
            });

            if (!data.foreign_worker_id) {
                queryClient.invalidateQueries({ queryKey: ['assessment_details'] });

                variables.setMainView('invite');

                calculateAssessmentPrograms({
                    assessmentId: data.id,
                    setIsProcessing: variables.setIsProcessing,
                });
            } else {
                calculateAssessmentPrograms({
                    assessmentId: data.id,
                    setIsProcessing: variables.setIsProcessing,
                    successCallback: () => {
                        navigate(`/assessments/${data.id}/immigration-recommendations`);
                    },
                });
            };
        },
        onError: (error: any) => {
            addToast({
                type: 'error',
                title: error.response.data.message,
            });
        },
        onSettled: (_, __, variables) => {
            variables.setIsProcessing(false);
        },
    });
};

export const useGetAssessmentDetails = (id: number | null) => {
    const queryFn = async (): Promise<IAssessmentDetails> => {
        const token = cookiesHelper.getCookie(`${process.env.REACT_APP_AUTH_TOKEN_NAME}`);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const response = await api.get(`assessments/details/${id}`);
        return response.data;
    };

    return useQuery({
        queryKey: ['assessment_details', id],
        queryFn,
        retry: 0,
        enabled: !!id
    });
};

export const useDeleteResume = () => {
    const { addToast } = useToast();
    const queryClient = useQueryClient();

    const mutationFn = async (data: HMIDeleteResume) => {
        const token = cookiesHelper.getCookie(`${process.env.REACT_APP_AUTH_TOKEN_NAME}`);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const response = await api.delete(`assessments/delete-resume/${data.id}`);
        return response.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsResumeDeleting(true);
        },
        onSuccess: () => {
            addToast({
                type: 'success',
                title: 'Resume deleted successfully.',
            });
            queryClient.invalidateQueries({ queryKey: ['assessment_details'] });
        },
        onError: (error: any) => {
            addToast({
                type: 'error',
                title: error.response.data.message,
            });
        },
        onSettled: (_, __, variables) => {
            variables.setIsResumeDeleting(false);
        },
    });
};

export const useDeleteJobDescription = () => {
    const { addToast } = useToast();
    const queryClient = useQueryClient();

    const mutationFn = async (data: HMIDeleteJobDescription) => {
        const token = cookiesHelper.getCookie(`${process.env.REACT_APP_AUTH_TOKEN_NAME}`);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const response = await api.delete(`assessments/delete-job-description/${data.id}`);
        return response.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsJobDescriptionDeleting(true);
        },
        onSuccess: () => {
            addToast({
                type: 'success',
                title: 'Job description deleted successfully.',
            });
            queryClient.invalidateQueries({ queryKey: ['assessment_details'] });
        },
        onError: (error: any) => {
            addToast({
                type: 'error',
                title: error.response.data.message,
            });
        },
        onSettled: (_, __, variables) => {
            variables.setIsJobDescriptionDeleting(false);
        },
    });
};

export const useUploadResume = () => {
    const { addToast } = useToast();
    const queryClient = useQueryClient();

    const mutationFn = async (data: HMIUploadResume) => {
        const token = cookiesHelper.getCookie(`${process.env.REACT_APP_AUTH_TOKEN_NAME}`);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const response = await api.patch(
            `assessments/upload-resume/${data.id}`,
            { file: data.file },
            { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        return response.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsResumeUploading(true);
        },
        onSuccess: () => {
            addToast({
                type: 'success',
                title: 'Resume uploaded successfully.',
            });
            queryClient.invalidateQueries({ queryKey: ['assessment_details'] });
        },
        onError: (error: any, variables) => {
            addToast({
                type: 'error',
                title: error.response.data.message,
            });
            if (variables.errorCallback) {
                variables.errorCallback();
            };
        },
        onSettled: (_, __, variables) => {
            variables.setIsResumeUploading(false);
        },
    });
};

export const useUploadJobDescription = () => {
    const { addToast } = useToast();
    const queryClient = useQueryClient();

    const mutationFn = async (data: HMIUploadJobDescription) => {
        const token = cookiesHelper.getCookie(`${process.env.REACT_APP_AUTH_TOKEN_NAME}`);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const response = await api.patch(
            `assessments/upload-job-description/${data.id}`,
            { file: data.file },
            { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        return response.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsJobDescriptionUploading(true);
        },
        onSuccess: () => {
            addToast({
                type: 'success',
                title: 'Job description uploaded successfully.',
            });
            queryClient.invalidateQueries({ queryKey: ['assessment_details'] });
        },
        onError: (error: any, variables) => {
            addToast({
                type: 'error',
                title: error.response.data.message,
            });
            if (variables.errorCallback) {
                variables.errorCallback();
            };
        },
        onSettled: (_, __, variables) => {
            variables.setIsJobDescriptionUploading(false);
        },
    });
};

export const useInviteWorkerFromAssessment = () => {
    const { addToast } = useToast();
    const queryClient = useQueryClient();

    const mutationFn = async (data: HMIIInviteFromAssessment) => {
        const token = cookiesHelper.getCookie(`${process.env.REACT_APP_AUTH_TOKEN_NAME}`);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const response = await api.post('/invitations/assessment/invite', data.payload);
        return response.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsInviting(true);
        },
        onSuccess: (_, variables) => {
            variables.setInvitationSuccess(true);
            addToast({
                type: 'success',
                title: 'Foreign worker added in the portal and invitation sent successfully.',
            });
            queryClient.invalidateQueries({ queryKey: ['assessment_details'] });
        },
        onError: (error: any) => {
            addToast({
                type: 'error',
                title: error.response.data.message,
            });
        },
        onSettled: (_, __, variables) => {
            variables.setIsInviting(false);
        },
    });
};